import { add, format, isBefore, startOfDay, startOfMonth } from 'date-fns';
import { Locale } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useImperativeHandle, useRef } from 'react';
import { ClassNames, DayPicker, Matcher } from 'react-day-picker';

import { cn } from '../../lib/utils';

import { Button, buttonVariants } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

// ---------- utils start ----------
function genMonths(locale: Pick<Locale, 'options' | 'localize' | 'formatLong'>) {
	return Array.from({ length: 12 }, (_, i) => ({
		value: i,
		label: format(new Date(2021, i), 'MMMM', { locale })
	}));
}

function genYears(startYear: number, yearRange = 50, minDate?: Date, maxDate?: Date) {
	const today = new Date();
	const endYear = today.getFullYear() + yearRange;
	return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i)
		.filter((year) => (!minDate || year >= minDate.getFullYear()) && (!maxDate || year <= maxDate.getFullYear()))
		.map((year) => ({
			value: year,
			label: year.toString()
		}));
}

function getInitialMonth(value?: Date, minDate?: Date, maxDate?: Date): Date {
	if (value) return value;

	const today = new Date();

	if (maxDate && maxDate < today) return maxDate;
	if (minDate && minDate > today) return minDate;

	return today;
}
// ---------- utils end ----------

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	yearRange = 50,
	minDate,
	maxDate,
	granularity,
	startYear = 1900,
	hideYearDropdown = false,
	...props
}: CalendarProps & {
	yearRange?: number;
	minDate?: Date;
	maxDate?: Date;
	granularity?: Granularity;
	startYear?: number;
	isCalendarReservations?: boolean;
	hideYearDropdown?: boolean;
}) {
	const MONTHS = React.useMemo(() => {
		let locale: Pick<Locale, 'options' | 'localize' | 'formatLong'> = enUS;
		const { options, localize, formatLong } = props.locale || {};
		if (options && localize && formatLong) {
			locale = {
				options,
				localize,
				formatLong
			};
		}
		if (props.isCalendarReservations && minDate) {
			return Array.from({ length: 12 - minDate.getMonth() }, (_, i) => ({
				value: minDate.getMonth() + i,
				label: format(new Date(2021, minDate.getMonth() + i), 'MMMM', { locale })
			}));
		}

		return genMonths(locale);
	}, [props.locale, minDate, props.isCalendarReservations]);

	const YEARS = React.useMemo(() => genYears(startYear, yearRange, minDate, maxDate), [startYear, yearRange]);

	const disabledDates: Matcher[] | undefined = React.useMemo(() => {
		const matchers: Matcher[] = [];
		if (minDate) {
			matchers.push({ before: minDate });
		}
		if (maxDate) {
			matchers.push({ after: maxDate });
		}
		return matchers.length > 0 ? matchers : undefined;
	}, [minDate, maxDate]);

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn('p-3 bg-white', className)}
			classNames={{
				months: 'flex flex-col sm:flex-row space-y-1 justify-center h-[250px] w-[270px]',
				month: 'flex flex-col items-center w-full h-full',
				month_caption: 'flex justify-center pt-1 relative items-center w-full h-9 mb-1',
				caption_label: 'text-sm font-medium w-[140px] text-center',
				nav: 'hidden',
				button_previous: 'hidden',
				button_next: 'hidden',
				month_grid: 'w-full h-[calc(100%-2.25rem)] flex flex-col gap-1',
				weekdays: cn('flex justify-center mb-1', props.showWeekNumber && 'justify-end'),
				weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
				week: 'flex w-full justify-center',
				day: 'h-8 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 rounded-1',
				day_button: cn(
					buttonVariants({ variant: 'ghost' }),
					'h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-l-md rounded-r-md hover:!bg-gray-100'
				),
				range_end: 'day-range-end',
				selected: 'bg-[#6640DA] text-primary-foreground rounded-l-md rounded-r-md pointer-events-none',
				today: 'ring-2 ring-accent rounded',
				outside:
					'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
				disabled: 'text-muted-foreground opacity-50',
				range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
				hidden: 'invisible',
				...classNames
			}}
			disabled={disabledDates}
			components={{
				Chevron: ({ ...props }) => (props.orientation === 'left' ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />),
				MonthCaption: ({ calendarMonth }) => {
					return (
						<div className="flex items-center justify-between w-full px-1">
							<div className="flex items-center gap-1 min-w-[120px]">
								<Button
									variant="ghost"
									className="h-7 w-7 bg-transparent p-0 hover:bg-transparent hover:opacity-100 opacity-50"
									onClick={() => {
										const newDate = new Date(calendarMonth.date);
										newDate.setMonth(newDate.getMonth() - 1);
										props.onMonthChange?.(newDate);
									}}
								>
									<ChevronLeft className="size-4" />
								</Button>
								<Select
									defaultValue={calendarMonth.date.getMonth().toString()}
									onValueChange={(value) => {
										const newDate = new Date(calendarMonth.date);
										newDate.setMonth(Number.parseInt(value, 10));
										props.onMonthChange?.(newDate);
									}}
								>
									<SelectTrigger
										id="month-trigger"
										className="shadow-none w-[100px] gap-1 border-none p-0 focus:bg-accent focus:text-accent-foreground"
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-white">
										{MONTHS.map((month) => (
											<SelectItem className="hover:!bg-gray-100" key={month.value} value={month.value.toString()}>
												{month.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Button
									variant="ghost"
									className="h-7 w-7 bg-transparent p-0 hover:bg-transparent hover:opacity-100 opacity-50"
									onClick={() => {
										const newDate = new Date(calendarMonth.date);
										newDate.setMonth(newDate.getMonth() + 1);
										props.onMonthChange?.(newDate);
									}}
								>
									<ChevronRight className="size-4" />
								</Button>
							</div>
							{(granularity === 'date' || granularity === 'time') && (
								<div className="flex items-center gap-1 min-w-[120px] justify-end">
									<Button
										variant="ghost"
										className="h-7 w-7 bg-transparent p-0 hover:bg-transparent hover:opacity-100 opacity-50"
										onClick={() => {
											const newDate = new Date(calendarMonth.date);
											newDate.setFullYear(newDate.getFullYear() - 1);
											props.onMonthChange?.(newDate);
										}}
									>
										<ChevronLeft className="size-4" />
									</Button>
									<Select
										defaultValue={calendarMonth.date.getFullYear().toString()}
										onValueChange={(value) => {
											const newDate = new Date(calendarMonth.date);
											newDate.setFullYear(Number.parseInt(value, 10));
											props.onMonthChange?.(newDate);
										}}
									>
										<SelectTrigger
											id="year-trigger"
											className="shadow-none w-[60px] gap-1 border-none p-0 focus:bg-accent focus:text-accent-foreground"
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-white">
											{YEARS.map((year) => (
												<SelectItem className="hover:!bg-gray-100" key={year.value} value={year.value.toString()}>
													{year.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<Button
										variant="ghost"
										className="h-7 w-7 bg-transparent p-0 hover:bg-transparent hover:opacity-100 opacity-50"
										onClick={() => {
											const newDate = new Date(calendarMonth.date);
											newDate.setFullYear(newDate.getFullYear() + 1);
											props.onMonthChange?.(newDate);
										}}
									>
										<ChevronRight className="size-4" />
									</Button>
								</div>
							)}
						</div>
					);
				}
			}}
			{...props}
		/>
	);
}
Calendar.displayName = 'Calendar';

interface TimePickerProps {
	date?: Date | null;
	onChange?: (date: Date | undefined) => void;
	hourCycle?: 12 | 24;
}

const TimePicker = React.forwardRef<HTMLDivElement, TimePickerProps>(({ date = new Date(), onChange, hourCycle = 24 }, ref) => {
	const [selectedHour, setSelectedHour] = React.useState<string>('');
	const [selectedMinute, setSelectedMinute] = React.useState<string>('');
	const [selectedPeriod, setSelectedPeriod] = React.useState<'AM' | 'PM'>('AM');

	React.useEffect(() => {
		if (date) {
			let hours = date.getHours();
			const minutes = date.getMinutes();

			if (hourCycle === 12) {
				setSelectedPeriod(hours >= 12 ? 'PM' : 'AM');
				hours = hours % 12 || 12; // Convert to 12-hour format
			}

			setSelectedHour(hours.toString().padStart(2, '0'));
			setSelectedMinute((Math.round(minutes / 5) * 5).toString().padStart(2, '0')); // Round to nearest 5
		}
	}, [date, hourCycle]);

	const handleHourChange = (value: string) => {
		setSelectedHour(value);
		updateDate(value, selectedMinute, selectedPeriod);
	};

	const handleMinuteChange = (value: string) => {
		setSelectedMinute(value);
		updateDate(selectedHour, value, selectedPeriod);
	};

	const handlePeriodChange = (value: 'AM' | 'PM') => {
		setSelectedPeriod(value);
		updateDate(selectedHour, selectedMinute, value);
	};

	const updateDate = (hourStr: string, minuteStr: string, period: 'AM' | 'PM') => {
		let hours = parseInt(hourStr, 10);
		const minutes = parseInt(minuteStr, 10);

		if (hourCycle === 12) {
			if (period === 'PM' && hours < 12) {
				hours += 12;
			} else if (period === 'AM' && hours === 12) {
				hours = 0;
			}
		}

		const newDate = new Date(date || new Date());
		newDate.setHours(hours);
		newDate.setMinutes(minutes);
		newDate.setSeconds(0);

		onChange?.(newDate);
	};

	const generateHourOptions = () => {
		const hours = [];
		const maxHour = hourCycle === 24 ? 23 : 12;
		const minHour = hourCycle === 24 ? 0 : 1;

		for (let i = minHour; i <= maxHour; i++) {
			hours.push(
				<SelectItem key={i} value={i.toString().padStart(2, '0')}>
					{i.toString().padStart(2, '0')}
				</SelectItem>
			);
		}
		return hours;
	};

	const generateMinuteOptions = () => {
		const minutes = [];
		for (let i = 0; i < 60; i += 5) {
			minutes.push(
				<SelectItem key={i} value={i.toString().padStart(2, '0')}>
					{i.toString().padStart(2, '0')}
				</SelectItem>
			);
		}
		return minutes;
	};

	return (
		<div ref={ref} className="flex items-center justify-center gap-2">
			{/* Hour Dropdown */}
			<Select value={selectedHour} onValueChange={handleHourChange}>
				<SelectTrigger id="hours-trigger" className="w-[70px]">
					<SelectValue placeholder="HH" />
				</SelectTrigger>
				<SelectContent>{generateHourOptions()}</SelectContent>
			</Select>

			{/* Separator */}
			<span>:</span>

			{/* Minute Dropdown */}
			<Select value={selectedMinute} onValueChange={handleMinuteChange}>
				<SelectTrigger id="minutes-trigger" className="w-[70px]">
					<SelectValue placeholder="MM" />
				</SelectTrigger>
				<SelectContent>{generateMinuteOptions()}</SelectContent>
			</Select>

			{/* AM/PM Selector for 12-hour format */}
			{hourCycle === 12 && (
				<Select value={selectedPeriod} onValueChange={handlePeriodChange}>
					<SelectTrigger id="time-format-trigger" className="w-[70px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="AM">AM</SelectItem>
						<SelectItem value="PM">PM</SelectItem>
					</SelectContent>
				</Select>
			)}
		</div>
	);
});
TimePicker.displayName = 'TimePicker';

type Granularity = 'date' | 'month' | 'time';

type DateTimePickerProps = {
	value?: Date;
	onChange?: (date: Date | undefined) => void;
	disabled?: boolean;
	/** showing `AM/PM` or not. */
	hourCycle?: 12 | 24;
	placeholder?: string;
	/**
	 * The year range will be: `This year + yearRange` and `this year - yearRange`.
	 * Default is 50.
	 * For example:
	 * This year is 2024, The year dropdown will be 1974 to 2024 which is generated by `2024 - 50 = 1974` and `2024 + 50 = 2074`.
	 * */
	yearRange?: number;
	/**
	 * The start year for the year dropdown.
	 * Default is the current year.
	 **/
	startYear?: number;
	/**
	 * The format is derived from the `date-fns` documentation.
	 * @reference https://date-fns.org/v3.6.0/docs/format
	 **/
	displayFormat?: { hour24?: string; hour12?: string };
	/**
	 * The granularity prop allows you to control the smallest unit that is displayed by DateTimePicker.
	 * It can be 'date', 'month', or 'time'.
	 **/
	granularity?: Granularity;
	className?: string;
	minDate?: Date;
	maxDate?: Date;
	error?: string;
	popoverSide?: 'top' | 'bottom' | 'left' | 'right';
	isCalendarReservations?: boolean;
	hideYearDropdown?: boolean;
	customTriggerContent?: React.ReactNode;
	calendarClassNames?: Partial<ClassNames>;
} & Pick<CalendarProps, 'locale' | 'weekStartsOn' | 'showWeekNumber' | 'showOutsideDays'>;

type DateTimePickerRef = {
	value?: Date;
} & Omit<HTMLButtonElement, 'value'>;

const DateTimePicker = React.forwardRef<Partial<DateTimePickerRef>, DateTimePickerProps>(
	(
		{
			locale = enUS,
			value,
			onChange,
			hourCycle = 12,
			yearRange = 50,
			disabled = false,
			displayFormat,
			placeholder = 'Pick a date',
			granularity = 'time',
			className,
			startYear = 1900,
			error,
			popoverSide,
			isCalendarReservations,
			hideYearDropdown = false,
			customTriggerContent,
			calendarClassNames,
			...props
		},
		ref
	) => {
		const [month, setMonth] = React.useState<Date>(() => getInitialMonth(value, props?.minDate, props?.maxDate));
		const buttonRef = useRef<HTMLButtonElement>(null);
		/**
		 * carry over the current time when a user clicks a new day
		 * instead of resetting to 00:00
		 */
		const handleSelect = (newDay: Date | undefined) => {
			if (!newDay) return;
			if (!value) {
				const now = new Date();
				const newDateWithTime = new Date(newDay.getFullYear(), newDay.getMonth(), newDay.getDate(), now.getHours(), now.getMinutes(), 0);
				onChange?.(newDateWithTime);
				setMonth(newDay);
				return;
			}
			const diff = newDay.getTime() - value.getTime();
			const diffInDays = diff / (1000 * 60 * 60 * 24);
			const newDateFull = add(value, { days: Math.ceil(diffInDays) });
			onChange?.(newDateFull);
			setMonth(newDateFull);
		};

		const handleMonthChange = (newMonth: Date) => {
			const today = startOfDay(new Date());
			const minMonth = startOfMonth(today);

			if (isBefore(newMonth, minMonth) && isCalendarReservations) {
				setMonth(minMonth);
			} else {
				setMonth(newMonth);
			}
		};

		useImperativeHandle(
			ref,
			() => ({
				...buttonRef.current,
				value
			}),
			[value]
		);

		const initHourFormat = React.useMemo(() => {
			let hour24Format = 'PPP';
			let hour12Format = 'PP';

			if (granularity === 'month') {
				hour24Format = 'dd MMMM'; // Day and month without year
				hour12Format = 'dd MMMM';
			} else if (granularity === 'time') {
				hour24Format = 'PPP HH:mm';
				hour12Format = 'PP hh:mm a';
			}

			if (hideYearDropdown) {
				hour24Format = 'dd MMMM';
				hour12Format = 'dd MMMM';
			}

			return {
				hour24: displayFormat?.hour24 ?? hour24Format,
				hour12: displayFormat?.hour12 ?? hour12Format
			};
		}, [granularity, displayFormat, hideYearDropdown]);

		let loc = enUS;
		const { options, localize, formatLong } = locale;
		if (options && localize && formatLong) {
			loc = {
				...enUS,
				options,
				localize,
				formatLong
			};
		}

		return (
			<Popover>
				<PopoverTrigger asChild disabled={disabled}>
					{customTriggerContent ? (
						customTriggerContent
					) : (
						<Button
							variant="outline"
							className={cn(
								'w-full justify-start text-left font-normal shadow-none rounded-[4px] p-2 text-sm text-gray-700',
								!value && 'text-muted-foreground',
								error !== undefined && 'rounded border border-error-500',
								className
							)}
							ref={buttonRef}
						>
							<CalendarIcon className="mr-2 size-4" />
							{value ? (
								format(value, hourCycle === 24 ? initHourFormat.hour24 : initHourFormat.hour12, {
									locale: loc
								})
							) : (
								<span className="text-sm font-normal">{placeholder}</span>
							)}
						</Button>
					)}
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" side={popoverSide}>
					<Calendar
						mode="single"
						selected={value}
						month={month}
						onSelect={(d) => handleSelect(d)}
						onMonthChange={isCalendarReservations || customTriggerContent ? handleMonthChange : handleSelect}
						startMonth={isCalendarReservations ? props.minDate : undefined}
						yearRange={yearRange}
						startYear={startYear}
						locale={locale}
						granularity={granularity}
						isCalendarReservations={isCalendarReservations}
						hideYearDropdown={hideYearDropdown}
						classNames={calendarClassNames}
						{...props}
					/>
					{granularity === 'time' && (
						<div className="border-t border-border p-3">
							<TimePicker onChange={onChange} date={value} hourCycle={hourCycle} />
						</div>
					)}
				</PopoverContent>
			</Popover>
		);
	}
);

DateTimePicker.displayName = 'DateTimePicker';

export { DateTimePicker, TimePicker };
export type { DateTimePickerProps, DateTimePickerRef };
